import { computed, Ref } from "vue";
import { maxBy as _maxBy } from "lodash-es";

import type { Event, EventDrilldown } from "@/shared/model/event";
import { Sport } from "@/shared/model/enums/sport";
import { PeriodType } from "@/shared/model/enums/period-type";
import { EventPreviewPeriod, EventPreviewScore, EventPreviewTime } from "@/shared/model/event-score-and-status";
import { FactType } from "@/shared/model/enums/fact-type";
import { PeriodPeriods } from "@/shared/model/period";
import { Esport } from "@/shared/model/enums/esport-game-names";
import { EventScoreboardColumn } from "@/shared/model/event-scoreboard";
import { Side } from "@/shared/model/enums/side";
import { CardType } from "@/shared/model/enums/card-type";
import { ScoreboardValueColor } from "@/shared/model/enums/scoreboard-value-color";

import useEventDetails from "@/shared/composables/use-event-details";

export default function useEventScoreAndStatus<T extends EventDrilldown>(event?: Ref<T>) {
    const { sportCode, countryName, homeCommentaryId, awayCommentaryId } = useEventDetails(event);
    const eventScore = computed(() => {
        if (!event.value?.liveNow) return;

        switch (sportCode.value) {
            case Sport.Football:
            case Sport.Futsal:
                return getFootballScore(event.value, homeCommentaryId.value, awayCommentaryId.value);
            default:
                return getDefaultScore(event.value, homeCommentaryId.value, awayCommentaryId.value);
        }
    });

    const eventTime = computed(() => {
        if (event.value?.liveNow) {
            switch (sportCode.value) {
                case Sport.Football:
                    return getFootballTime(event.value);
                case Sport.Tennis:
                case Sport.Badminton:
                case Sport.Volleyball:
                    return getTennisTime(event.value);
                case Sport.IceHockey:
                    return getHockeyTime(event.value);
                case Sport.Basketball:
                    return getBasketballTime(event.value);
                case Sport.Esports:
                    return getEsportsTime(
                        event.value,
                        countryName.value,
                        homeCommentaryId.value,
                        awayCommentaryId.value,
                    );
                default:
                    return {} as EventPreviewTime;
            }
        } else {
            return getPrematchDate(event.value);
        }
    });

    const eventPeriods = computed(() => {
        if (!event.value?.liveNow) return;

        switch (sportCode.value) {
            case Sport.Football:
                return getFootballPeriods(event.value, homeCommentaryId.value, awayCommentaryId.value);
            case Sport.Tennis:
            case Sport.TableTennis:
            case Sport.Basketball:
            case Sport.Volleyball:
                return getTennisPeriods(event.value, homeCommentaryId.value, awayCommentaryId.value);
            case Sport.Badminton:
                return getBasketballPeriods(event.value, homeCommentaryId.value, awayCommentaryId.value);
            case Sport.Esports:
                return getEsportsPeriods(
                    event.value,
                    countryName.value,
                    homeCommentaryId.value,
                    awayCommentaryId.value,
                );
            default:
                return [];
        }
    });

    const scoreboard = computed<EventScoreboardColumn[]>(() => {
        if (!event.value?.commentary || !event.value?.liveNow) return [];

        switch (sportCode.value) {
            case Sport.Football:
                return getFootballScoreboard(
                    event.value,
                    eventTime.value,
                    eventScore.value,
                    homeCommentaryId.value,
                    awayCommentaryId.value,
                );
            case Sport.Futsal:
                return getFutsalScoreboard(
                    event.value,
                    eventTime.value,
                    eventScore.value,
                    homeCommentaryId.value,
                    awayCommentaryId.value,
                );
            case Sport.Volleyball:
                return getVolleyballScoreboard(
                    event.value,
                    eventTime.value,
                    eventScore.value,
                    homeCommentaryId.value,
                    awayCommentaryId.value,
                );
            case Sport.Tennis:
            case Sport.TableTennis:
                return getTennisScoreboard(
                    event.value,
                    eventTime.value,
                    eventScore.value,
                    homeCommentaryId.value,
                    awayCommentaryId.value,
                );
            case Sport.IceHockey:
                return getIceHockeyScoreboard(
                    event.value,
                    eventTime.value,
                    eventScore.value,
                    homeCommentaryId.value,
                    awayCommentaryId.value,
                );
            case Sport.Esports:
                return getEsportsScoreboard(
                    event.value,
                    countryName.value,
                    eventTime.value,
                    eventScore.value,
                    homeCommentaryId.value,
                    awayCommentaryId.value,
                );
            default:
                return getDefaultScoreboard(event.value, eventTime.value, eventScore.value);
        }
    });

    return {
        eventScore,
        eventTime,
        eventPeriods,
        scoreboard,
    };
}

function getFootballScore<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewScore {
    const scoreHome = event?.commentary?.facts?.find((item) => {
        return item.type === FactType.Score && item.participantId === homeCommentaryId;
    })?.value;
    const scoreAway = event?.commentary?.facts?.find((item) => {
        return item.type === FactType.Score && item.participantId === awayCommentaryId;
    })?.value;

    const redCardsHome = event?.commentary?.facts?.find((item) => {
        return item.type === FactType.RedCards && item.participantId === homeCommentaryId;
    })?.value;
    const redCardsAway = event?.commentary?.facts?.find((item) => {
        return item.type === FactType.RedCards && item.participantId === awayCommentaryId;
    })?.value;

    return {
        scoreHome: scoreHome,
        redCardsHome: redCardsHome,
        scoreAway: scoreAway,
        redCardsAway: redCardsAway,
    };
}

function getDefaultScore<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewScore {
    const scoreHome = event?.commentary?.facts?.find((item) => {
        return item.type === FactType.Score && item.participantId === homeCommentaryId;
    })?.value;
    const scoreAway = event?.commentary?.facts?.find((item) => {
        return item.type === FactType.Score && item.participantId === awayCommentaryId;
    })?.value;

    return {
        scoreHome: scoreHome,
        scoreAway: scoreAway,
    };
}

export function getPrematchDate<T extends Event>(event: T): EventPreviewTime {
    if (!event || !event?.startTime) return {} as EventPreviewTime;

    const eventDate = new Date(event?.startTime);

    const isDateToday = eventDate.toDateString() === new Date().toDateString();
    const isDateTomorrow =
        eventDate.toDateString() === new Date(new Date().setDate(new Date().getDate() + 1)).toDateString();

    let startDate: string;

    if (isDateToday) {
        startDate = "Dnes";
    } else if (isDateTomorrow) {
        startDate = "Zítra";
    } else {
        const weekday = new Intl.DateTimeFormat("cs-CZ", { weekday: "short", day: "numeric", month: "numeric" }).format(
            eventDate,
        );

        startDate = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    }

    const startTime = new Intl.DateTimeFormat("cs-CZ", { hour: "numeric", minute: "2-digit" }).format(eventDate);

    const year =
        new Date().getFullYear() === eventDate.getFullYear()
            ? ""
            : new Intl.DateTimeFormat("cs-CZ", { year: "numeric" }).format(eventDate);

    return {
        startDate: startDate,
        startTime: startTime,
        startYear: year,
    };
}

function getFootballTime<T extends EventDrilldown>(event: T): EventPreviewTime {
    const firstHalf = event.commentary?.periods?.find((item) => item.type === PeriodType.FirstHalf);
    const secondHalf = event.commentary?.periods?.find((item) => item.type === PeriodType.SecondHalf);

    if (secondHalf) {
        let minutes = Math.ceil(45 + secondHalf.currentActiveOffset / 60);
        let overtime = 0;

        if (minutes > 90) {
            overtime = minutes - 90;
            minutes = 90;
        }

        return {
            periodNumber: 2,
            periodName: "poločas",
            minutesPlayed: minutes,
            minutesOvertime: overtime,
        };
    } else if (firstHalf) {
        let minutes = Math.ceil(firstHalf.currentActiveOffset / 60);
        let overtime = 0;

        if (minutes > 45) {
            overtime = minutes - 45;
            minutes = 45;
        }

        return {
            periodNumber: 1,
            periodName: "poločas",
            minutesPlayed: minutes,
            minutesOvertime: overtime,
        };
    }
}

function getTennisTime<T extends EventDrilldown>(event: T): EventPreviewTime {
    const lastPeriod = _maxBy(event.commentary?.periods, "periodIndex");

    return {
        periodNumber: lastPeriod?.periodIndex,
        periodName: lastPeriod?.periodIndex ? "set" : null,
    };
}

function getHockeyTime<T extends EventDrilldown>(event: T): EventPreviewTime {
    const period1 = event.commentary?.periods?.find((item) => item.type === PeriodType.Period1);
    const period2 = event.commentary?.periods?.find((item) => item.type === PeriodType.Period2);
    const period3 = event.commentary?.periods?.find((item) => item.type === PeriodType.Period3);

    if (period3) {
        return {
            periodNumber: 3,
            periodName: "třetina",
            minutesPlayed: Math.ceil(period3.clock.offset / 60),
        };
    } else if (period2) {
        return {
            periodNumber: 2,
            periodName: "třetina",
            minutesPlayed: Math.ceil(period2.clock.offset / 60),
        };
    } else if (period1) {
        return {
            periodNumber: 1,
            periodName: "třetina",
            minutesPlayed: Math.ceil(period1.clock.offset / 60),
        };
    }
}

function getBasketballTime<T extends EventDrilldown>(event: T): EventPreviewTime {
    const quarter1 = event.commentary?.periods?.find((item) => item.type === PeriodType.Quarter1);
    const quarter2 = event.commentary?.periods?.find((item) => item.type === PeriodType.Quarter2);
    const quarter3 = event.commentary?.periods?.find((item) => item.type === PeriodType.Quarter3);
    const quarter4 = event.commentary?.periods?.find((item) => item.type === PeriodType.Quarter4);

    if (quarter4) {
        return {
            periodNumber: 4,
            periodName: "čtvrtina",
            minutesPlayed: Math.ceil(quarter4.clock.offset / 60),
        };
    } else if (quarter3) {
        return {
            periodNumber: 3,
            periodName: "čtvrtina",
            minutesPlayed: Math.ceil(quarter3.clock.offset / 60),
        };
    } else if (quarter2) {
        return {
            periodNumber: 2,
            periodName: "čtvrtina",
            minutesPlayed: Math.ceil(quarter2.clock.offset / 60),
        };
    } else if (quarter1) {
        return {
            periodNumber: 1,
            periodName: "čtvrtina",
            minutesPlayed: Math.ceil(quarter1.clock.offset / 60),
        };
    }
}

function getEsportsTime<T extends EventDrilldown>(
    event: T,
    countryName: string,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewTime {
    const gameName = countryName?.toLowerCase();

    if (gameName?.includes(Esport.EFotbal)) {
        return getFootballTime(event);
    } else {
        switch (gameName) {
            case Esport.CS:
            case Esport.Valorant:
                return getEsportGameTime(event, homeCommentaryId, awayCommentaryId, "mapa");
            case Esport.LoL:
            case Esport.KingsOfGlory:
            case Esport.Dota:
                return getEsportGameTime(event, homeCommentaryId, awayCommentaryId, "hra");
            default:
                return {} as EventPreviewTime;
        }
    }
}

function getEsportGameTime<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
    periodName: string,
): EventPreviewTime {
    const score = getDefaultScore(event, homeCommentaryId, awayCommentaryId);

    const scoreHome = Number(score.scoreHome);
    const scoreAway = Number(score.scoreAway);

    return {
        periodNumber: scoreHome + scoreAway + 1,
        periodName: periodName,
    };
}

function getFootballPeriods<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewPeriod[] {
    return event?.commentary?.periods?.map((period) => {
        return {
            scoreHome: period?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            scoreAway: period?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,
        };
    });
}

function getTennisPeriods<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewPeriod[] {
    const sets = event?.commentary?.periods
        ?.filter((period) => period.type === PeriodType.Set)
        .toSorted((a, b) => a?.periodIndex - b?.periodIndex);

    return sets?.map((period, index) => {
        const lastGem = _maxBy(period.periods, "periodIndex");

        return {
            scoreHome: period?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            scoreAway: period?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,

            pointsHome: lastGem?.facts?.find(
                (fact) => fact.type === FactType.DisplayScore && fact.participantId === homeCommentaryId,
            )?.value,
            pointsAway: lastGem?.facts?.find(
                (fact) => fact.type === FactType.DisplayScore && fact.participantId === awayCommentaryId,
            )?.value,

            starHome: event?.commentary?.facts?.find((fact) => fact.type === FactType.Serve)?.value === "HOME",
            starAway: event?.commentary?.facts?.find((fact) => fact.type === FactType.Serve)?.value === "AWAY",

            homeColor: index === sets.length - 1 ? "orange" : null,
            awayColor: index === sets.length - 1 ? "orange" : null,
        };
    });
}

function createBasketballPeriod(
    period: PeriodPeriods,
    homeCommentaryId: string,
    awayCommentaryId: string,
    index: number,
    totalPeriods: number,
): EventPreviewPeriod {
    return {
        scoreHome: period.facts?.find((fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId)
            ?.value,
        scoreAway: period.facts?.find((fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId)
            ?.value,
        homeColor: index === totalPeriods - 1 ? "orange" : null,
        awayColor: index === totalPeriods - 1 ? "orange" : null,
    };
}

function getBasketballPeriods<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewPeriod[] {
    const periods: EventPreviewPeriod[] = [];
    const periodTypes = [PeriodType.Quarter1, PeriodType.Quarter2, PeriodType.Quarter3, PeriodType.Quarter4];

    periodTypes.forEach((type, index) => {
        const period = event.commentary?.periods?.find((item) => item.type === type);
        if (period) {
            periods.push(createBasketballPeriod(period, homeCommentaryId, awayCommentaryId, index, periodTypes.length));
        }
    });

    return periods;
}

function getEsportsPeriods<T extends EventDrilldown>(
    event: T,
    countryName: string,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewPeriod[] {
    const gameName = countryName?.toLowerCase();

    if (gameName?.includes(Esport.EFotbal)) {
        return getFootballPeriods(event, homeCommentaryId, awayCommentaryId);
    } else {
        switch (gameName) {
            case Esport.CS:
                return getCounterStrikePeriods(event, homeCommentaryId, awayCommentaryId);
            case Esport.Valorant:
                return getValorantPeriods(event, homeCommentaryId, awayCommentaryId);
            default:
                return [];
        }
    }
}

function getCounterStrikePeriods(
    event: EventDrilldown,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewPeriod[] {
    const periods = event?.commentary?.periods?.slice().reverse();
    const activePeriod = periods?.find((period) => period?.facts?.some((fact) => fact.type === FactType.CurrentRound));

    return [
        {
            scoreHome: activePeriod?.facts?.find(
                (fact) => fact.type === FactType.RoundsWon && fact.participantId === homeCommentaryId,
            )?.value,
            homeColor: valueColor(
                activePeriod?.facts?.find(
                    (fact) => fact.type === FactType.TeamDescriptor && fact.participantId === homeCommentaryId,
                )?.value,
            ),
            scoreAway: activePeriod?.facts?.find(
                (fact) => fact.type === FactType.RoundsWon && fact.participantId === awayCommentaryId,
            )?.value,
            awayColor: valueColor(
                activePeriod?.facts?.find(
                    (fact) => fact.type === FactType.TeamDescriptor && fact.participantId === awayCommentaryId,
                )?.value,
            ),
        },
    ];
}

function getValorantPeriods(
    event: EventDrilldown,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventPreviewPeriod[] {
    const periods = event?.commentary?.periods?.slice().reverse();
    const activePeriod = periods?.find((period) => period?.facts?.some((fact) => fact.type === FactType.CurrentRound));

    return [
        {
            scoreHome: activePeriod?.facts?.find(
                (fact) => fact.type === FactType.RoundsWon && fact.participantId === homeCommentaryId,
            )?.value,
            scoreAway: activePeriod?.facts?.find(
                (fact) => fact.type === FactType.RoundsWon && fact.participantId === awayCommentaryId,
            )?.value,
        },
    ];
}

function getPlayersColumn<T extends EventDrilldown>(event: T, eventTime: EventPreviewTime): EventScoreboardColumn {
    let minutesPlayed = "";

    if (eventTime?.minutesPlayed > 0) {
        if (eventTime?.minutesOvertime > 0) {
            minutesPlayed = `${eventTime?.minutesPlayed}'+${eventTime?.minutesOvertime}`;
        } else {
            minutesPlayed += `${eventTime?.minutesPlayed}'`;
        }
    }

    return {
        name:
            eventTime?.periodNumber && eventTime?.periodName
                ? `${eventTime.periodNumber}. ${eventTime.periodName}`
                : "",
        nameSecondValue: minutesPlayed,
        homeValue: event.teams.find((team) => team.side === Side.Home)?.name,
        awayValue: event.teams.find((team) => team.side === Side.Away)?.name,
    };
}

function getTotalScoreColumn(eventScore: EventPreviewScore): EventScoreboardColumn {
    return {
        name: "Stav",
        homeValue: eventScore.scoreHome,
        awayValue: eventScore.scoreAway,
    };
}

function getFootballPeriodsColumns<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn[] {
    const firstHalf = event?.commentary.periods?.find((item) => item.type === PeriodType.FirstHalf);
    const secondHalf = event?.commentary.periods?.find((item) => item.type === PeriodType.SecondHalf);

    const periods: EventScoreboardColumn[] = [];

    if (firstHalf) {
        periods.push({
            name: "1.P",
            homeValue: firstHalf?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            awayValue: secondHalf?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,
        });
    }

    if (secondHalf) {
        periods.push({
            name: "2.P",
            homeValue: secondHalf?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            awayValue: secondHalf?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,
        });
    }

    return periods;
}

function getCornersColumn<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn {
    return {
        icon: "corner",
        homeValue: event?.commentary?.facts?.find(
            (fact) => fact.type === FactType.Corners && fact.participantId === homeCommentaryId,
        )?.value,
        awayValue: event?.commentary?.facts?.find(
            (fact) => fact.type === FactType.Corners && fact.participantId === awayCommentaryId,
        )?.value,
    };
}

function getYellowCardsColumn<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn {
    return {
        card: CardType.Yellow,
        homeValue: event?.commentary?.facts?.find(
            (fact) => fact.type === FactType.YellowCards && fact.participantId === homeCommentaryId,
        )?.value,
        awayValue: event?.commentary?.facts?.find(
            (fact) => fact.type === FactType.YellowCards && fact.participantId === awayCommentaryId,
        )?.value,
    };
}

function getRedCardsColumn<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn {
    return {
        card: CardType.Red,
        homeValue: event?.commentary?.facts?.find(
            (fact) => fact.type === FactType.RedCards && fact.participantId === homeCommentaryId,
        )?.value,
        awayValue: event?.commentary?.facts?.find(
            (fact) => fact.type === FactType.RedCards && fact.participantId === awayCommentaryId,
        )?.value,
    };
}

function getPeriodsColumns<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
    filterBySets = false,
) {
    let periods = event?.commentary.periods;

    if (filterBySets) {
        periods = periods.filter((period) => period.type === PeriodType.Set);
    }

    return periods
        ?.toSorted((a, b) => a.periodIndex - b.periodIndex)
        ?.map(
            (period): EventScoreboardColumn => ({
                name: period.periodIndex.toString(),
                homeValue: period?.facts?.find(
                    (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
                )?.value,
                awayValue: period?.facts?.find(
                    (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
                )?.value,
            }),
        );
}

function getCurrentSetColumn<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn {
    const currentSet = _maxBy(event?.commentary?.periods, "periodIndex");
    const lastSetLastGem = _maxBy(currentSet?.periods, "periodIndex");

    return {
        name: "A",
        homeValue: lastSetLastGem?.facts?.find(
            (fact) => fact.type === FactType.DisplayScore && fact.participantId === homeCommentaryId,
        )?.value,
        awayValue: lastSetLastGem?.facts?.find(
            (fact) => fact.type === FactType.DisplayScore && fact.participantId === awayCommentaryId,
        )?.value,
    };
}

function getIceHockeyPeriodsColumns<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn[] {
    const firstThird = event?.commentary.periods?.find((period) => period.type === PeriodType.Period1);
    const secondThird = event?.commentary.periods?.find((period) => period.type === PeriodType.Period2);
    const thirdThird = event?.commentary.periods?.find((period) => period.type === PeriodType.Period3);

    const periods: EventScoreboardColumn[] = [];

    if (firstThird) {
        periods.push({
            name: "1.T",
            homeValue: firstThird?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            awayValue: firstThird?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,
        });
    }

    if (secondThird) {
        periods.push({
            name: "2.T",
            homeValue: secondThird?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            awayValue: secondThird?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,
        });
    }

    if (thirdThird) {
        periods.push({
            name: "3.T",
            homeValue: thirdThird?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            awayValue: thirdThird?.facts?.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,
        });
    }

    return periods;
}

function getFootballScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    return [
        getPlayersColumn(event, eventTime),
        getTotalScoreColumn(eventScore),
        ...getFootballPeriodsColumns(event, homeCommentaryId, awayCommentaryId),
        getCornersColumn(event, homeCommentaryId, awayCommentaryId),
        getYellowCardsColumn(event, homeCommentaryId, awayCommentaryId),
        getRedCardsColumn(event, homeCommentaryId, awayCommentaryId),
    ];
}

function getFutsalScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    return [
        getPlayersColumn(event, eventTime),
        getTotalScoreColumn(eventScore),
        ...getFootballPeriodsColumns(event, homeCommentaryId, awayCommentaryId),
        getYellowCardsColumn(event, homeCommentaryId, awayCommentaryId),
        getRedCardsColumn(event, homeCommentaryId, awayCommentaryId),
    ];
}

function getVolleyballScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    return [
        getPlayersColumn(event, eventTime),
        getTotalScoreColumn(eventScore),
        ...getPeriodsColumns(event, homeCommentaryId, awayCommentaryId),
    ];
}

function getTennisScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    return [
        getPlayersColumn(event, eventTime),
        getTotalScoreColumn(eventScore),
        ...getPeriodsColumns(event, homeCommentaryId, awayCommentaryId, true),
        getCurrentSetColumn(event, homeCommentaryId, awayCommentaryId),
    ];
}

function getIceHockeyScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    return [
        getPlayersColumn(event, eventTime),
        getTotalScoreColumn(eventScore),
        ...getIceHockeyPeriodsColumns(event, homeCommentaryId, awayCommentaryId),
    ];
}

function getShooterPlayersColumn<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
): EventScoreboardColumn {
    const currentRound = event?.commentary.periods.find(
        (period) => period.type === PeriodType.CurrentRound,
    )?.periodIndex;

    let name = "";

    if (eventTime?.periodNumber && eventTime?.periodName) {
        name += `${eventTime.periodNumber}. ${eventTime.periodName}`;
    }
    if (currentRound) {
        name += ` ${currentRound}. kolo`;
    }

    return {
        name: name,
        homeValue: event.teams.find((team) => team.side === Side.Home)?.name,
        awayValue: event.teams.find((team) => team.side === Side.Away)?.name,
    };
}

function getShooterPeriodsColumns<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn[] {
    return event?.commentary?.periods
        ?.toSorted((a, b) => a.periodIndex - b.periodIndex)
        ?.map((period, index) => {
            const isPeriodLast = event?.commentary?.periods?.length === index + 1;

            return {
                name: period.periodIndex.toString(),
                homeValue: period?.facts?.find(
                    (fact) => fact.type === FactType.RoundsWon && fact.participantId === homeCommentaryId,
                )?.value,
                homeColor: isPeriodLast
                    ? valueColor(
                          period?.facts?.find(
                              (fact) =>
                                  fact.type === FactType.TeamDescriptor && fact.participantId === homeCommentaryId,
                          )?.value,
                      )
                    : ("" as ScoreboardValueColor),
                awayValue: period?.facts?.find(
                    (fact) => fact.type === FactType.RoundsWon && fact.participantId === awayCommentaryId,
                )?.value,
                awayColor: isPeriodLast
                    ? valueColor(
                          period?.facts?.find(
                              (fact) =>
                                  fact.type === FactType.TeamDescriptor && fact.participantId === awayCommentaryId,
                          )?.value,
                      )
                    : ("" as ScoreboardValueColor),
            };
        });
}

function getShooterScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    return [
        getShooterPlayersColumn(event, eventTime),
        getTotalScoreColumn(eventScore),
        ...getShooterPeriodsColumns(event, homeCommentaryId, awayCommentaryId),
    ];
}

function getMobaPeriodsColumns<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn[] {
    return event?.commentary.periods?.map((period) => {
        return {
            name: period.periodIndex.toString(),
            homeValue: period.facts.find(
                (fact) => fact.type === FactType.Score && fact.participantId === homeCommentaryId,
            )?.value,
            awayValue: period.facts.find(
                (fact) => fact.type === FactType.Score && fact.participantId === awayCommentaryId,
            )?.value,
        };
    });
}

function getMobaKillsColumn<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn {
    const lastPeriod = _maxBy(event?.commentary?.periods, "periodIndex");

    return {
        icon: "sword",
        homeValue: lastPeriod?.facts?.find(
            (fact) => fact.type === FactType.Kills && fact.participantId === homeCommentaryId,
        )?.value,
        awayValue: lastPeriod?.facts?.find(
            (fact) => fact.type === FactType.Kills && fact.participantId === awayCommentaryId,
        )?.value,
    };
}

function getMobaTowersColumn<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn {
    const lastPeriod = _maxBy(event?.commentary?.periods, "periodIndex");

    return {
        icon: "tower",
        homeValue: lastPeriod?.facts?.find(
            (fact) =>
                (fact.type === FactType.DestroyedTurrets || fact.type === FactType.DestroyedTowers) &&
                fact.participantId === homeCommentaryId,
        )?.value,
        awayValue: lastPeriod?.facts?.find(
            (fact) =>
                (fact.type === FactType.DestroyedTurrets || fact.type === FactType.DestroyedTowers) &&
                fact.participantId === awayCommentaryId,
        )?.value,
    };
}

function getMobaGoldColumn<T extends EventDrilldown>(
    event: T,
    homeCommentaryId: string,
    awayCommentaryId: string,
): EventScoreboardColumn {
    const lastPeriod = _maxBy(event?.commentary?.periods, "periodIndex");

    const homeValue = lastPeriod?.facts?.find(
        (fact) => fact.type === FactType.Gold && fact.participantId === homeCommentaryId,
    )?.value;

    const awayValue = lastPeriod?.facts?.find(
        (fact) => fact.type === FactType.Gold && fact.participantId === awayCommentaryId,
    )?.value;

    return {
        icon: "coin",
        homeValue: homeValue ? `${homeValue}K` : null,
        awayValue: awayValue ? `${awayValue}K` : null,
    };
}

function getMobaScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    return [
        getPlayersColumn(event, eventTime),
        getTotalScoreColumn(eventScore),
        ...getMobaPeriodsColumns(event, homeCommentaryId, awayCommentaryId),
        getMobaKillsColumn(event, homeCommentaryId, awayCommentaryId),
        getMobaTowersColumn(event, homeCommentaryId, awayCommentaryId),
        getMobaGoldColumn(event, homeCommentaryId, awayCommentaryId),
    ];
}

function getEsportsScoreboard<T extends EventDrilldown>(
    event: T,
    countryName: string,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
    homeCommentaryId: string,
    awayCommentaryId: string,
) {
    const gameName = countryName?.toLowerCase();

    if (gameName?.includes(Esport.EFotbal)) {
        return getFootballScoreboard(event, eventTime, eventScore, homeCommentaryId, awayCommentaryId);
    } else {
        switch (gameName) {
            case Esport.CS:
            case Esport.Valorant:
                return getShooterScoreboard(event, eventTime, eventScore, homeCommentaryId, awayCommentaryId);
            case Esport.LoL:
            case Esport.Dota:
            case Esport.KingsOfGlory:
                return getMobaScoreboard(event, eventTime, eventScore, homeCommentaryId, awayCommentaryId);
            default:
                return getDefaultScoreboard(event, eventTime, eventScore);
        }
    }
}

function getDefaultScoreboard<T extends EventDrilldown>(
    event: T,
    eventTime: EventPreviewTime,
    eventScore: EventPreviewScore,
) {
    return [getPlayersColumn(event, eventTime), getTotalScoreColumn(eventScore)];
}

function valueColor(side: string): ScoreboardValueColor {
    if (side === "terrorist") {
        return "orange";
    } else if (side === "counterTerrorist") {
        return "blue";
    }
}
